import { track } from 'src/lib/analytics/utils';
import * as Decoration from 'src/replicant/decoration';
import { DecorationRoomID } from 'src/replicant/decoration/decoration.ruleset';
import StateObserver from 'src/StateObserver';

/**
 * Decoration start event
 *
 * The player has entered the Decoration screen
 *
 *  @param room - Name of the room the player will be decorating
 */
export function decorationStartEvent(room: DecorationRoomID) {
  track('DecorationStart', {
    room,
    ownedDecorationsForRoom: ownedDecorationsForRoom(room),
  });
}

/**
 * Decoration finish event
 *
 * The player has finished decoration, either canceled or updated
 *
 * @param DecorationFinish.room - Name of the room the player will be decorating
 * @param DecorationFinish.canceled True: the player canceled the decoration session
 *                            False: the player finished the decoration session by tapping OK button
 * @param DecorationFinish.ownedDecorationsForRoom - Number of decoration items the player owns for the room, including the ones that the player has newly purchased
 * @param DecorationFinish.slotsEdited - The number of slots the player edited during the decoration session. Put 0 if the player canceled or didn't make any change
 */
export function decorationFinishEvent(properties: {
  room: DecorationRoomID;
  canceled: boolean;
  slotsEdited: number;
}) {
  const { room, canceled, slotsEdited } = properties;

  track('DecorationFinish', {
    room,
    canceled,
    ownedDecorationsForRoom: ownedDecorationsForRoom(room),
    slotsEdited,
    ...getEquippedDecorationsSnapshot(),
  });
}

/**
 * Number of decoration items the player owns for the room,
 * including the ones that are given to the player by default
 */
function ownedDecorationsForRoom(room: DecorationRoomID): number {
  const { user } = StateObserver.getState();
  const { isDecorationPurchased, getAvailableDecorations } = Decoration.getters;

  const available = getAvailableDecorations(room).filter((item) => {
    return item.price === 0 || isDecorationPurchased(user, item.id);
  });

  return available.length;
}

/**
 * Build a snapshot of equipped decoration items for each room.
 * Maps numeric slot IDs to human-readable names with item/tier IDs.
 */
function getEquippedDecorationsSnapshot(): {
  living: Record<string, string>;
  toilet: Record<string, string>;
} {
  const { user } = StateObserver.getState();
  const { getSelectedDecorationsState } = Decoration.getters;

  const equipped = getSelectedDecorationsState(user);

  const initial = {
    living: {
      1: 'default',
      2: 'default',
      3: 'default',
      4: 'default',
      5: 'default',
      6: 'default',
      7: 'default',
      8: 'default',
    },
    toilet: {
      1: 'default',
      2: 'default',
      3: 'default',
      4: 'default',
      5: 'default',
    },
  } as Record<DecorationRoomID, Record<number, string>>;

  if (!equipped) {
    return {
      living: mapLivingSlots(initial.living),
      toilet: mapToiletSlots(initial.toilet),
    };
  }

  const grouped = equipped.reduce((acc, item) => {
    const room = item.room as DecorationRoomID;
    const slot = item.slot;

    if (!acc[room]) {
      acc[room] = {};
    }

    acc[room][slot] = item.tierId ?? item.id;
    return acc;
  }, initial);

  return {
    living: mapLivingSlots(grouped.living),
    toilet: mapToiletSlots(grouped.toilet),
  };
}

function mapLivingSlots(slots: Record<number, string>): Record<string, string> {
  return {
    wallpaper: slots[1],
    floor: slots[2],
    aboveFireplace: slots[3],
    painting: slots[4],
    fireplace: slots[5],
    tree: slots[6],
    couch: slots[7],
    sideTable: slots[8],
  };
}

function mapToiletSlots(slots: Record<number, string>): Record<string, string> {
  return {
    wallpaper: slots[1],
    floor: slots[2],
    arch: slots[3],
    toilet: slots[4],
    toiletOrnament: slots[5],
  };
}
